import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";

import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { TagList } from "@/shared/components/TagList";
import { FormattedDateTime } from "@/shared/datetime";
import { type SessionRecord } from "@/shared/domain/sessions";
import { formatNanoDuration, formatNanoTimestamp } from "@/shared/time";
import { type ISO_8601_DATETIME } from "@/types";

import { EditableSessionDescription } from "./EditableSessionDescription";
import { EditableSessionName } from "./EditableSessionName";
import styles from "./SessionDetail.module.css";
import { SessionTabBar } from "./SessionTabBar";

export interface SessionChromeProps {
  /** URL-derived session id. Used as the heading when ``session`` is ``null``. */
  sessionId: string;
  /**
   * Resolved session record, or ``null`` when the parent's ``useSession``
   * fetch is pending or failed. When ``null`` the heading falls back to
   * ``sessionId`` and the description, tags, and metadata rows are omitted;
   * the tab strip renders unconditionally because it reads its active
   * value from the URL.
   */
  session: SessionRecord | null;
}

/**
 * Title row, description, tags, metadata strip, and tab bar for
 * ``/sessions/:sessionId``. Rendered on both the success and error
 * branches so the user sees the same heading hierarchy whether or not
 * the session record resolved.
 */
export function SessionChrome({ sessionId, session }: SessionChromeProps) {
  return (
    <div className={styles.headerSection}>
      <div className={styles.titleRow}>
        {session ? (
          <EditableSessionName
            name={session.name ?? ""}
            sessionId={session.session_id}
            orgId={session.org_id}
          />
        ) : (
          <h1 className={styles.title}>{sessionId}</h1>
        )}
      </div>

      <SessionDescription session={session} />
      <SessionHeaderTags session={session} />
      <SessionMetadataRow session={session} />

      <SessionTabBar />
    </div>
  );
}

function SessionDescription({ session }: { session: SessionRecord | null }) {
  if (!session) {
    return null;
  }
  return (
    <div className={styles.descriptionRow}>
      <EditableSessionDescription
        description={session.description}
        sessionId={session.session_id}
        orgId={session.org_id}
      />
    </div>
  );
}

function SessionHeaderTags({ session }: { session: SessionRecord | null }) {
  if (!session || !session.tags.length) {
    return null;
  }
  return (
    <div className={styles.tagsRow}>
      <TagList tags={session.tags} maxDisplayCount={5} />
    </div>
  );
}

function SessionMetadataRow({ session }: { session: SessionRecord | null }) {
  if (!session) {
    return null;
  }

  return (
    <div className={styles.metadataRow}>
      <span className={styles.metadataField}>
        <span className={styles.metadataLabel}>Session ID:</span>
        <span className={styles.metadataText}>{session.session_id}</span>
      </span>

      <SessionBoundsMetadata
        minTimestampNs={session.min_timestamp_ns}
        maxTimestampNs={session.max_timestamp_ns}
      />
      <CreatedDateMetadata created={session.created} />

      <span className={styles.metadataField}>
        <span className={styles.metadataLabel}>Created By:</span>
        <RobotoPrincipalUnderlineLink principal={session.created_by} icon />
      </span>
    </div>
  );
}

function CreatedDateMetadata({
  created,
}: {
  created: ISO_8601_DATETIME | null;
}) {
  if (!created) {
    return null;
  }

  return (
    <span className={styles.metadataField}>
      <CalendarTodayOutlinedIcon className={styles.metadataIcon} />
      <span className={styles.metadataLabel}>Created:</span>
      <span className={styles.metadataText}>
        <FormattedDateTime value={created} time="none" />
      </span>
    </span>
  );
}

function SessionBoundsMetadata({
  minTimestampNs,
  maxTimestampNs,
}: {
  minTimestampNs: bigint | null;
  maxTimestampNs: bigint | null;
}) {
  if (minTimestampNs === null || maxTimestampNs === null) {
    return null;
  }

  return (
    <>
      <span className={styles.metadataField}>
        <ScheduleOutlinedIcon className={styles.metadataIcon} />
        <span className={styles.metadataLabel}>Started:</span>
        <span className={styles.metadataText}>
          {formatNanoTimestamp(minTimestampNs)}
        </span>
      </span>

      <span className={styles.metadataField}>
        <ScheduleOutlinedIcon className={styles.metadataIcon} />
        <span className={styles.metadataLabel}>Ended:</span>
        <span className={styles.metadataText}>
          {formatNanoTimestamp(maxTimestampNs)}
        </span>
      </span>

      <span className={styles.metadataField}>
        <TimerOutlinedIcon className={styles.metadataIcon} />
        <span className={styles.metadataLabel}>Duration:</span>
        <span className={styles.metadataText}>
          {formatNanoDuration(maxTimestampNs - minTimestampNs)}
        </span>
      </span>
    </>
  );
}
